import {
  IntegrationChannel,
  LookupEntityFieldType,
  NotifyConversationOpenedPayload,
  ThirdPartyWSMethod
} from './websocket/types';
import { ReactElement, useEffect, useRef } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { fetchContact } from '../../utils/fetchContact';
import { useCassieWebsocket } from './websocket/CassieWebsocketContext';
import usePageVisible from './hooks/usePageVisible';
import { usePrevious } from './hooks/usePrevious';

interface CassieContainerPropsInterface {
  contactId: string | undefined;
}

function CassieContainer({ contactId }: CassieContainerPropsInterface): ReactElement {
  //TODO: Refactor the use of getting contacts
  const contact = contactId ? fetchContact(contactId) : undefined;
  const prevContact = usePrevious(contact);
  const { invoke } = useCassieWebsocket();
  const { isPageVisible } = usePageVisible();
  const prevIsPageVisible = usePrevious(isPageVisible);
  const lastSentContactId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!prevIsPageVisible && isPageVisible) {
      void invoke(ThirdPartyWSMethod.NOTIFY_THIRD_PARTY_FOCUSED, {
        thirdPartyName: IntegrationChannel.CATO
      });
      datadogLogs.logger.info(`WEBSOCKET event sent to Cassie`, {
        event: ThirdPartyWSMethod.NOTIFY_THIRD_PARTY_FOCUSED
      });
    }
  }, [prevIsPageVisible, isPageVisible, invoke]);

  useEffect(() => {
    if (!contactId) {
      lastSentContactId.current = undefined;
      return;
    }

    if (lastSentContactId.current === contactId) {
      return;
    }

    //TODO: Refactor the use of getting contacts
    if (!contact) return;

    //TODO: Refactor defensive fetching of contact attributes
    const intentInteractionId = contact.getAttributes().intent_interaction_id?.value;
    const customerId = contact.getAttributes().customer_id?.value;

    let queueName: string | undefined;
    try {
      queueName = contact.getQueue().name;
    } catch {
      queueName = undefined;
    }

    let phoneNumber: string | undefined;
    try {
      phoneNumber = contact.getInitialConnection()?.getEndpoint()?.phoneNumber;
    } catch {
      phoneNumber = undefined;
    }

    let customerEmail: string | undefined;
    try {
      customerEmail = (contact.getCustomerEndpoint() as { emailAddress?: string })?.emailAddress;
    } catch {
      customerEmail = undefined;
    }

    const wsPayload: Partial<NotifyConversationOpenedPayload> = {
      channel: IntegrationChannel.CATO,
      channelConversationId: contactId,
      conversationSubsidiaryId: queueName ? parseInt(queueName) : undefined,
      intentInteractionId: intentInteractionId,
      ...(customerId && {
        lookupCustomer: {
          fieldType: LookupEntityFieldType.CUSTOMER_ID,
          fieldValue: customerId
        }
      })
    };
    if (customerEmail) {
      wsPayload.lookupCustomer = {
        fieldType: LookupEntityFieldType.EMAIL,
        fieldValue: customerEmail
      };
      wsPayload.visualIdentifier = customerEmail;
    } else if (phoneNumber) {
      wsPayload.lookupCustomer = {
        fieldType: LookupEntityFieldType.TELEPHONE,
        fieldValue: phoneNumber
      };
      wsPayload.visualIdentifier = phoneNumber;
    }

    void invoke(ThirdPartyWSMethod.NOTIFY_CONVERSATION_OPENED, wsPayload as NotifyConversationOpenedPayload);
    datadogLogs.logger.info(`WEBSOCKET event sent to Cassie`, {
      event: ThirdPartyWSMethod.NOTIFY_CONVERSATION_OPENED
    });

    lastSentContactId.current = contactId;
  }, [contactId, invoke]);

  useEffect(() => {
    if (prevContact && !contact) {
      void invoke(ThirdPartyWSMethod.NOTIFY_CONVERSATION_CLOSED, { channel: IntegrationChannel.CATO });
      datadogLogs.logger.info(`WEBSOCKET event sent to Cassie`, {
        event: ThirdPartyWSMethod.NOTIFY_CONVERSATION_CLOSED
      });
    }
  }, [prevContact, contact, invoke]);

  return <></>;
}

export default CassieContainer;
